import {PayloadAction, createSlice} from '@reduxjs/toolkit';

/**
 * My weather on the account (backend#266).
 *
 * Both apps reset this slice at LOGOUT, with everything else that belongs to
 * the account: the sync at the next sign-in only adds, so anything left here
 * would be uploaded again.
 */
export type MyWeatherState = {
  /**
   * The account's My weather order, `kind:id` tokens with places named by
   * their device hash (`MyWeather.fromServerOrder`). Null until the account
   * has one. Places not on the account yet are not in it.
   */
  order: string[] | null;
  /**
   * The order was edited on this device and not sent yet
   * (`useMyWeatherOrderSync`). Meanwhile /me leaves it alone: it carries the
   * account's older order.
   */
  orderPending: boolean;
  /**
   * The station order the pending edit goes with, sent beside it as
   * `station_ids`; null to let the server rebuild that one from the order.
   * Recorded with the edit, never read from the live station order: a /me
   * landing meanwhile resets that one to the account's older value.
   */
  orderStationIds: number[] | null;
  /**
   * Station favourites this device held when the user signed in, kept for
   * the sync to add to the account: the /me that follows a sign-in replaces
   * the device's favourites with the account's.
   */
  pendingStationIds: number[];
};

const initialState: MyWeatherState = {
  order: null,
  orderPending: false,
  orderStationIds: null,
  pendingStationIds: [],
};

const myWeatherSlice = createSlice({
  name: 'myWeather',
  initialState,
  reducers: {
    /** The account's order, from /me. */
    setMyWeatherOrder: (state, {payload}: PayloadAction<string[] | null>) => {
      state.order = payload;
    },
    /** A reorder made here, to send, with the station order it goes with. */
    editMyWeatherOrder: (
      state,
      {payload}: PayloadAction<{order: string[]; stationIds: number[] | null}>,
    ) => {
      state.order = payload.order;
      state.orderStationIds = payload.stationIds;
      state.orderPending = true;
    },
    /**
     * The account took the edit `payload`. One made since it was sent stays
     * pending, for the next request: a new order, or the same order with a
     * new station order (a move in My stations).
     */
    markMyWeatherOrderSynced: (
      state,
      {payload}: PayloadAction<{order: string[]; stationIds: number[] | null}>,
    ) => {
      const same = <T>(a: readonly T[] | null, b: readonly T[] | null) =>
        a === b ||
        (a !== null &&
          b !== null &&
          a.length === b.length &&
          a.every((item, i) => item === b[i]));
      if (
        same(state.order ?? [], payload.order) &&
        same(state.orderStationIds, payload.stationIds)
      ) {
        state.orderPending = false;
        state.orderStationIds = null;
      }
    },
    /**
     * The sync went off (#421): nothing sends the edit waiting, and kept for
     * when it comes back on, it would overwrite whatever the account changed
     * meanwhile. The order shown stays until a /me brings the account's.
     */
    discardMyWeatherOrderEdit: (state) => {
      state.orderPending = false;
      state.orderStationIds = null;
    },
    addPendingStationIds: (state, {payload}: PayloadAction<number[]>) => {
      state.pendingStationIds = [
        ...new Set([...state.pendingStationIds, ...payload]),
      ];
    },
    /** The ones the account now holds. Any added since stay pending. */
    clearPendingStationIds: (state, {payload}: PayloadAction<number[]>) => {
      state.pendingStationIds = state.pendingStationIds.filter(
        (id) => !payload.includes(id),
      );
    },
  },
});

export const {
  setMyWeatherOrder,
  editMyWeatherOrder,
  markMyWeatherOrderSynced,
  discardMyWeatherOrderEdit,
  addPendingStationIds,
  clearPendingStationIds,
} = myWeatherSlice.actions;

export default myWeatherSlice.reducer;
